import { Alert, AlertDescription, AlertTitle } from '@artic/ui/alert';
import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@artic/ui/card';
import { Checkbox } from '@artic/ui/checkbox';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { Logo } from '@artic/ui/logo';
import { Separator } from '@artic/ui/separator';
import { Switch } from '@artic/ui/switch';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/debug/')({
  component: DebugPage,
  staticData: { section: 'Debug', breadcrumb: 'Components' },
});

function DebugPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-8">
      <div>
        <h1 className="text-3xl font-bold">Shadcn Style Showcase</h1>
        <Logo />
        <Logo size="sm" />
        <Logo size="lg" />
        <Logo size="xl" />
        <div className="flex flex-wrap gap-3 bg-amber-950">
          <Logo variant="inverse" />
          <Logo variant="inverse" size="sm" />
          <Logo variant="inverse" size="lg" />
          <Logo variant="inverse" size="xl" />
        </div>
      </div>

      <Separator />

      {/* Buttons */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Buttons
        </h2>
        <div className="flex flex-wrap gap-3">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
          <Button disabled>Disabled</Button>
        </div>
      </section>

      <Separator />

      {/* Badges */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Badges
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="destructive">Destructive</Badge>
          <Badge variant="outline">Outline</Badge>
        </div>
      </section>

      <Separator />

      {/* Inputs */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Form controls
        </h2>
        <div className="grid max-w-lg grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Email</Label>
            <Input type="email" placeholder="you@example.com" />
          </div>
          <div className="space-y-1">
            <Label>Disabled</Label>
            <Input disabled placeholder="Not editable" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="toggle" />
            <Label htmlFor="toggle">Enable notifications</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="check" />
            <Label htmlFor="check">Accept terms</Label>
          </div>
        </div>
      </section>

      <Separator />

      {/* Card */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">Card</h2>
        <Card className="max-w-sm">
          <CardHeader>
            <CardTitle>Card title</CardTitle>
            <CardDescription>Card description goes here.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm">
              This is the card body. Radius, background, and border colours all come from your
              active preset.
            </p>
          </CardContent>
          <CardFooter className="gap-2">
            <Button size="sm">Save</Button>
            <Button size="sm" variant="outline">
              Cancel
            </Button>
          </CardFooter>
        </Card>
      </section>

      <Separator />

      {/* Alerts */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Alerts
        </h2>
        <div className="max-w-lg space-y-3">
          <Alert>
            <AlertTitle>Default alert</AlertTitle>
            <AlertDescription>Your preset colours should show up here.</AlertDescription>
          </Alert>
          <Alert variant="destructive">
            <AlertTitle>Destructive alert</AlertTitle>
            <AlertDescription>This uses the destructive colour from your theme.</AlertDescription>
          </Alert>
        </div>
      </section>

      <Separator />

      {/* Typography */}
      <section className="space-y-3">
        <h2 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Typography
        </h2>
        <div className="space-y-2">
          <p className="text-4xl font-bold">Heading 1</p>
          <p className="text-2xl font-semibold">Heading 2</p>
          <p className="text-xl font-medium">Heading 3</p>
          <p className="text-base">Body text — normal weight</p>
          <p className="text-muted-foreground text-sm">Small muted text</p>
          <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-sm">inline code</code>
        </div>
      </section>
    </div>
  );
}
